import React, { useState } from 'react';
import { FONT_OPTIONS } from './fontOptions';
import { AlignIcon, ALIGN_LINES, ALIGN_LABELS } from './AlignIcons';
import ColorPicker from './ColorPicker';
import { Input, Select } from '../Common';
import HeaderFooterEditor from './HeaderFooterEditor';

// Shared by the body font picker and the header/footer text pickers, so the
// three offer the same families. Anything outside PDFKit's core set is mapped
// to the nearest one it has when the PDF renders (see pdfFontFor); DOCX uses
// the name as given.

export default function PageSettingsPanel({ styles, pageSettings, logoData, onUpdateStyles, onUpdatePageSettings, onLogoUpload, onLogoRemove }) {
  const [activeTab, setActiveTab] = useState('styles');

  const tabStyle = (tab) => ({
    padding: '6px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
    background: activeTab === tab ? 'var(--primary)' : 'transparent',
    color: activeTab === tab ? '#fff' : 'var(--text-secondary)',
    border: 'none', borderRadius: 4, transition: 'all 0.15s'
  });

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-secondary)', marginBottom: 8 }}>
        Page Settings
      </div>

      <div style={{ display: 'flex', gap: 2, marginBottom: 12, flexWrap: 'wrap' }}>
        <button style={tabStyle('styles')} onClick={() => setActiveTab('styles')}>Styles</button>
        <button style={tabStyle('page')} onClick={() => setActiveTab('page')}>Page</button>
      </div>

      {activeTab === 'styles' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Select label="Body Font" value={styles.bodyFont || 'Helvetica'}
            onChange={e => onUpdateStyles({ bodyFont: e.target.value })}
            options={FONT_OPTIONS} style={{ marginBottom: 8 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <Input label="Heading Size" type="number" value={styles.headingSize || 20}
              onChange={e => onUpdateStyles({ headingSize: parseInt(e.target.value) || 20 })}
              style={{ marginBottom: 8, flex: 1 }} />
            <Input label="Body Size" type="number" value={styles.bodySize || 11}
              onChange={e => onUpdateStyles({ bodySize: parseInt(e.target.value) || 11 })}
              style={{ marginBottom: 8, flex: 1 }} />
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>Body Text Alignment</label>
            <div style={{ display: 'flex', gap: 4 }}>
              {['left', 'center', 'right', 'justify'].map(val => (
                <button key={val} onClick={() => onUpdateStyles({ bodyAlignment: val })} style={{
                  padding: '4px 10px', fontSize: 14, fontWeight: 600,
                  background: (styles.bodyAlignment || 'justify') === val ? 'var(--primary)' : 'var(--bg-input)',
                  color: (styles.bodyAlignment || 'justify') === val ? '#fff' : 'var(--text)',
                  border: '1px solid var(--border)', borderRadius: 'var(--radius)', cursor: 'pointer',
                  flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4
                }} title={ALIGN_LABELS[val]}>
                  <AlignIcon lines={ALIGN_LINES[val]} /> <span style={{ fontSize: 10 }}>{val.charAt(0).toUpperCase() + val.slice(1)}</span>
                </button>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>Primary Color</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ColorPicker label="Primary colour" size={32}
                value={styles.primaryColor || '#3b82f6'}
                onChange={c => onUpdateStyles({ primaryColor: c || '#3b82f6' })} />
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{styles.primaryColor || '#3b82f6'}</span>
            </div>
          </div>
          <div style={{ marginBottom: 8 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: 'var(--text-secondary)' }}>Logo</label>
            {logoData && (
              <div style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 }}>
                <img src={logoData} alt="Logo" style={{ maxHeight: 40, borderRadius: 4, border: '1px solid var(--border)' }} />
                <button onClick={onLogoRemove}
                  style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer', color: '#ef4444', fontSize: 11, padding: '2px 8px' }}>
                  Remove
                </button>
              </div>
            )}
            <input type="file" accept="image/*" onChange={onLogoUpload} style={{ fontSize: 12, width: '100%' }} />
          </div>
        </div>
      )}

      {activeTab === 'page' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Select label="Page Size" value={pageSettings.size || 'A4'}
            onChange={e => onUpdatePageSettings({ size: e.target.value })}
            options={[
              { value: 'A4', label: 'A4 (210 x 297mm)' },
              { value: 'LETTER', label: 'Letter (8.5 x 11in)' }
            ]} style={{ marginBottom: 8 }} />
          <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)', marginBottom: 4 }}>Margins (pt)</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
            {['top', 'bottom', 'left', 'right'].map(side => (
              <Input key={side} label={side.charAt(0).toUpperCase() + side.slice(1)} type="number"
                value={pageSettings.margins?.[side] ?? 50}
                onChange={e => onUpdatePageSettings({ margins: { ...pageSettings.margins, [side]: parseInt(e.target.value) || 50 } })}
                style={{ marginBottom: 4 }} />
            ))}
          </div>
        </div>
      )}

    </div>
  );
}
